import config from './config';

export type SiteMatchDirection = 'injection' | 'offtake';
export type SiteDirection = 'injection' | 'offtake' | 'both';
export type SiteDecision = 'create' | 'attach';

export interface SiteMatchAddress {
  street: string | null;
  number: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
}

export interface SiteMatchLeg {
  sdp_id: number;
  installation_id: string | null;
  ean: string;
}

export interface SiteMatch {
  site_id: number;
  name: string;
  address: SiteMatchAddress;
  offtake: SiteMatchLeg | null;
  injection: SiteMatchLeg | null;
}

export interface SiteMatchResponse {
  match: SiteMatch | null;
  suggestedName: string;
}

// The optional `site` block added to the POST /contracts/addContract body. Absent ⇒
// unchanged onboarding behaviour (backward compatible with the pre-site payload).
export interface SitePayload {
  direction: SiteDirection;
  decision: SiteDecision;
  name: string;
  site_id?: number;
}

// Nested in the existing addContract envelope at response.site when a site block was sent.
export interface SiteResult {
  status: 'created' | 'attached' | 'failed';
  site_id: number;
  error: string | null;
}

export class SiteMatchError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'SiteMatchError';
    this.status = status;
  }
}

/**
 * GET /api/v1/sdp/companies/:companyId/site-match?ean=<digits>&direction=injection|offtake
 *
 * Company-scoped (403 if the caller is not linked). Only call for a SINGLE direction —
 * "both" always creates a new site, so no lookup is made there. `match` is null when no
 * existing site at the SDP's address should be preselected.
 */
export const fetchSiteMatch = async (
  companyId: number,
  ean: string,
  direction: SiteMatchDirection,
  token: string
): Promise<SiteMatchResponse> => {
  const url = `${config.API_SERVICES}/api/v1/sdp/companies/${companyId}/site-match?ean=${encodeURIComponent(
    ean
  )}&direction=${direction}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new SiteMatchError(response.status, `Failed to fetch site match: ${response.status}`);
  }

  return response.json();
};
